import { useEffect, useRef } from 'react';

export default function SolutionSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-slide-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = sectionRef.current;
    if (section) {
      const elements = section.querySelectorAll('.animate-on-scroll');
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  const responseElements = [
    { label: 'What the app hears', icon: 'ri-focus-3-line' },
    { label: 'The deeper issue', icon: 'ri-search-line' },
    { label: 'A mindset reframe', icon: 'ri-refresh-line' },
    { label: 'A powerful coaching question', icon: 'ri-question-mark' },
    { label: 'A practical next step', icon: 'ri-footprint-line' },
    { label: 'A simple 24-hour action commitment', icon: 'ri-calendar-check-line' },
  ];

  return (
    <section ref={sectionRef} className="relative py-20 md:py-28 bg-midnight">
      <div className="absolute inset-0 bg-gradient-to-b from-midnight via-royal-navy/10 to-midnight" />
      <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full bg-champagne-gold/3 blur-3xl" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="animate-on-scroll opacity-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-champagne-gold/30 bg-champagne-gold/10 mb-6">
              <i className="ri-lightbulb-line text-champagne-gold text-sm" />
              <span className="text-champagne-gold text-sm font-medium">The Solution</span>
            </div>

            <h2 className="animate-on-scroll opacity-0 font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-warm-white mb-6">
              Your personal AI coaching companion for{' '}
              <span className="gold-gradient-text">clarity, confidence, and action.</span>
            </h2>

            <p className="animate-on-scroll opacity-0 text-silver-grey text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
              TNT Coach AI helps you work through challenges using different coaching styles. Whether you need
              motivation, discipline, calm thinking, business strategy, confidence, leadership, habits, or purpose,
              the app gives you a practical response designed to move you forward.
            </p>
          </div>

          <div className="animate-on-scroll opacity-0 bg-royal-navy/20 border border-champagne-gold/20 rounded-2xl p-6 md:p-8">
            <p className="text-champagne-gold text-sm font-semibold uppercase tracking-wider mb-6 text-center">
              Each coaching response includes:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {responseElements.map((element, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 rounded-xl bg-midnight/40 border border-royal-navy/20 hover:border-champagne-gold/30 transition-colors duration-300"
                >
                  <div className="w-10 h-10 rounded-lg gold-gradient flex items-center justify-center flex-shrink-0">
                    <i className={`${element.icon} text-midnight text-lg`} />
                  </div>
                  <p className="text-warm-white text-sm font-medium">{element.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}